/**
 * JobItemsView - Renders the expanded items list for a single job
 *
 * Extracted from ContagensListView to keep it under 500 lines.
 * Handles both 'cards' and 'list' view modes.
 */
import React from 'react';
import { Package } from 'lucide-react';
import { BarcodeByType, TipoCodigoIcon, TIPOS_CODIGO, type TipoCodigo, type TipoCodigoInfo } from './BarcodeByType';

interface ContagemItem {
  id: number;
  codigo_produto: string;
  descricao: string;
  lote: string;
  data_validade: string;
  quantidade_sistema: number;
  gtin?: string;
  gtin_cadastro?: string;
  codigo_cliente_produto?: string;
  quantidade_coletar?: number;
  unidade: string;
  faixa_aging?: string;
  dias_para_vencer?: number;
  status_processamento?: string;
  cliente_nome_fantasia?: string;
  com_regime?: boolean;
}

interface JobItemsViewProps {
  items: ContagemItem[];
  rawItemsCount: number;
  viewMode: 'cards' | 'list';
  formatDate: (dateString: string) => string;
}

const getTipoCodigo = (item: ContagemItem): TipoCodigoInfo => {
  const tipos: TipoCodigo[] = ['A', 'B', 'C', 'D'];
  const tipoIndex = item.id % 4;
  return TIPOS_CODIGO[tipos[tipoIndex]];
};

export const JobItemsView: React.FC<JobItemsViewProps> = ({ items, rawItemsCount, viewMode, formatDate }) => {
  if (items.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>Nenhum item encontrado nesta contagem</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="text-sm font-semibold text-gray-700 mb-3">
        {items.length} itens{rawItemsCount !== items.length ? ` (de ${rawItemsCount})` : ''} nesta contagem:
      </div>

      {viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, index) => {
            const barcodeValue = item.gtin_cadastro || '';
            const tipoInfo = getTipoCodigo(item);
            return (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition">
                <div className="flex justify-between items-start mb-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${tipoInfo.bgCor} ${tipoInfo.cor}`}>
                    <TipoCodigoIcon tipo={tipoInfo.icon} />
                    Tipo {tipoInfo.tipo} - {tipoInfo.nome}
                  </span>
                  <span className="text-xs text-gray-400" title={tipoInfo.descricao}>
                    {tipoInfo.leituras} leitura{tipoInfo.leituras > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="mb-3">
                  <div className="font-semibold text-gray-900 text-sm">
                    {item.descricao || item.codigo_produto || 'Produto não identificado'}
                  </div>
                  <div className="text-xs text-gray-500 font-mono">Código: {item.codigo_produto || '-'}</div>
                </div>
                <div className="space-y-1 text-xs mb-3">
                  {item.lote && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Lote:</span>
                      <span className="font-medium text-blue-600">{item.lote}</span>
                    </div>
                  )}
                  {item.data_validade && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Validade:</span>
                      <span className="font-medium text-green-600">{formatDate(item.data_validade)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Qtd Sistema:</span>
                    <span className="font-bold text-purple-600">{Math.round(item.quantidade_sistema)}</span>
                  </div>
                  {item.cliente_nome_fantasia && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Cliente:</span>
                      <span className="font-medium text-amber-600">{item.cliente_nome_fantasia}</span>
                    </div>
                  )}
                </div>
                <div className="bg-gray-50 rounded p-2 flex flex-col items-center">
                  <span className="text-xs text-gray-500 mb-1">
                    {tipoInfo.tipo === 'A' ? '2 Códigos (GTIN + Lote)' :
                     tipoInfo.tipo === 'B' ? 'GS1-128 Completo' :
                     tipoInfo.tipo === 'C' ? 'DataMatrix GS1' : 'EAN-13 / GTIN'}
                  </span>
                  <BarcodeByType
                    tipo={tipoInfo.tipo}
                    gtin={barcodeValue}
                    lote={item.lote || ''}
                    validade={item.data_validade || ''}
                    productName={item.descricao || item.codigo_produto}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg overflow-hidden">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo Código</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lote</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Validade</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qtd Sistema</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">GTIN</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código de Barras</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((item, index) => {
                const barcodeValue = item.gtin_cadastro || '';
                const tipoInfo = getTipoCodigo(item);
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${tipoInfo.bgCor} ${tipoInfo.cor}`} title={tipoInfo.descricao}>
                        <TipoCodigoIcon tipo={tipoInfo.icon} />
                        {tipoInfo.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">{item.codigo_produto || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.descricao || '-'}</td>
                    <td className="px-4 py-3 text-sm text-blue-600 font-medium">{item.lote || '-'}</td>
                    <td className="px-4 py-3 text-sm text-green-600 font-medium">
                      {item.data_validade ? formatDate(item.data_validade) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-purple-600">{item.quantidade_sistema ? Math.round(item.quantidade_sistema) : '-'}</td>
                    <td className="px-4 py-3 text-sm text-amber-600">{item.cliente_nome_fantasia || '-'}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-700">{item.gtin_cadastro || '-'}</td>
                    <td className="px-4 py-3">
                      <BarcodeByType
                        tipo={tipoInfo.tipo}
                        gtin={barcodeValue}
                        lote={item.lote || ''}
                        validade={item.data_validade || ''}
                        productName={item.descricao || item.codigo_produto}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
